// RaporGör Demo — Hibrit (Bloomberg / SAP Fiori) stil
// Flat, muted, data-first. Navy primary + slate neutrals + ince amber warning accent.
// IBM Plex Sans + tabular numerals zorunlu.

export interface ThemeColors {
  primary: string;
  primaryHover: string;
  primaryLight: string;
  accent: string; // warning amber

  pageBg: string;
  cardBg: string;
  sidebarBg: string;
  headerBg: string;
  hoverBg: string;
  selectedBg: string;
  accentBg: string;
  inputBg: string;

  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  textDisabled: string;
  textInverse: string;

  border: string;
  borderLight: string;

  positive: string;
  negative: string;
  warning: string;
  error: string;
  errorBg: string;
  errorBorder: string;
  success: string;

  tierTemel: string;
  tierOrta: string;
  tierIleri: string;

  shadowColor: string;
  shadowColorStrong: string;
}

export const demoTheme: ThemeColors = {
  primary: '#0f172a',
  primaryHover: '#1e293b',
  primaryLight: '#475569',
  accent: '#b45309',

  pageBg: '#f8fafc',
  cardBg: '#ffffff',
  sidebarBg: '#ffffff',
  headerBg: '#ffffff',
  hoverBg: '#f1f5f9',
  selectedBg: '#e2e8f0',
  accentBg: '#f8fafc',
  inputBg: '#ffffff',

  textPrimary: '#0f172a',
  textSecondary: '#475569',
  textMuted: '#64748b',
  textDisabled: '#94a3b8',
  textInverse: '#ffffff',

  border: '#e2e8f0',
  borderLight: '#f1f5f9',

  positive: '#16a34a',
  negative: '#b91c1c',
  warning: '#b45309',
  error: '#b91c1c',
  errorBg: '#fef2f2',
  errorBorder: '#fecaca',
  success: '#16a34a',

  tierTemel: '#475569',
  tierOrta: '#b45309',
  tierIleri: '#6b21a8',

  shadowColor: 'rgba(15, 23, 42, 0.04)',
  shadowColorStrong: 'rgba(15, 23, 42, 0.08)',
};

export const fontFamilySans = "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
export const fontFamilyMono = "'IBM Plex Mono', 'SF Mono', ui-monospace, Menlo, monospace";

export function getThemeCSSVariables(theme: ThemeColors): string {
  return `
    --color-primary: ${theme.primary};
    --color-primary-hover: ${theme.primaryHover};
    --color-primary-light: ${theme.primaryLight};
    --color-accent: ${theme.accent};

    --color-page-bg: ${theme.pageBg};
    --color-card-bg: ${theme.cardBg};
    --color-sidebar-bg: ${theme.sidebarBg};
    --color-header-bg: ${theme.headerBg};
    --color-hover-bg: ${theme.hoverBg};
    --color-selected-bg: ${theme.selectedBg};
    --color-accent-bg: ${theme.accentBg};
    --color-input-bg: ${theme.inputBg};

    --color-text-primary: ${theme.textPrimary};
    --color-text-secondary: ${theme.textSecondary};
    --color-text-muted: ${theme.textMuted};
    --color-text-disabled: ${theme.textDisabled};
    --color-text-inverse: ${theme.textInverse};

    --color-border: ${theme.border};
    --color-border-light: ${theme.borderLight};

    --color-positive: ${theme.positive};
    --color-negative: ${theme.negative};
    --color-warning: ${theme.warning};
    --color-error: ${theme.error};
    --color-error-bg: ${theme.errorBg};
    --color-error-border: ${theme.errorBorder};
    --color-success: ${theme.success};

    --color-tier-temel: ${theme.tierTemel};
    --color-tier-orta: ${theme.tierOrta};
    --color-tier-ileri: ${theme.tierIleri};

    --shadow-color: ${theme.shadowColor};
    --shadow-color-strong: ${theme.shadowColorStrong};

    --font-sans: ${fontFamilySans};
    --font-mono: ${fontFamilyMono};
  `;
}

// Ant Design token mapping — dar radius + IBM Plex + hairline borders.
export const antdTheme = {
  token: {
    colorPrimary: demoTheme.primary,
    colorInfo: demoTheme.primary,
    colorSuccess: demoTheme.positive,
    colorWarning: demoTheme.warning,
    colorError: demoTheme.negative,
    colorTextBase: demoTheme.textPrimary,
    colorBgBase: demoTheme.cardBg,
    colorBorder: demoTheme.border,
    borderRadius: 4,
    borderRadiusLG: 4,
    borderRadiusSM: 2,
    fontFamily: fontFamilySans,
    fontSize: 13,
    fontSizeLG: 14,
    fontSizeSM: 12,
    controlHeight: 32,
    controlHeightSM: 28,
    lineHeight: 1.45,
    wireframe: false,
  },
  components: {
    Menu: {
      itemSelectedBg: demoTheme.selectedBg,
      itemSelectedColor: demoTheme.primary,
      itemHoverBg: demoTheme.hoverBg,
    },
    Layout: {
      headerBg: demoTheme.headerBg,
      siderBg: demoTheme.sidebarBg,
      bodyBg: demoTheme.pageBg,
    },
    Button: {
      borderRadius: 4,
      fontWeight: 500,
    },
    Card: {
      borderRadiusLG: 4,
      boxShadowTertiary: 'none',
    },
    Table: {
      headerBg: demoTheme.hoverBg,
      headerColor: demoTheme.textSecondary,
      rowHoverBg: demoTheme.hoverBg,
      fontSize: 13,
    },
    Input: { borderRadius: 4 },
    Select: { borderRadius: 4 },
    DatePicker: { borderRadius: 4 },
    Tooltip: { borderRadius: 4 },
    Modal: { borderRadiusLG: 4 },
  },
};
